'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AlertCircle, BookOpen, Paintbrush, RefreshCw, Sparkles } from 'lucide-react';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { bookRequestHeaders } from '@/components/manga/request-headers';
import {
  comicReaderState,
  comicPreviewFromBook,
  comicPreviewPollMs,
  pollFailureAction,
  bandChangeRetry,
} from '@/components/manga/reader-state';
import { trackCreate } from '@/utils/create-flow-analytics';
import { httpFailureReason, type FailureReason } from '@/utils/analytics-events';
import { READER_BAND_ORDER, bandAgeLabel, type ReaderBand } from '@/utils/manga/comic-story-profile';
import { PICTURE_BOOK_FLOW_STYLES, pictureBookStyleName } from '@/utils/manga/picture-book-style';
import { pictureBookResumeTarget } from '@/lib/books/product-type';
import type { PictureBookStyle } from '@/types/manga';
import {
  EDITED_MESSAGE,
  FIXED_TITLE_NOTE,
  NOT_CHANGEABLE_MESSAGE,
  LEGAL_MATCH_NOTICE,
  REDRAW_LIMIT_MESSAGE,
  PREVIEW_SLOW_MESSAGE,
  PREVIEW_SLOW_MS,
  bandChangeIsRedraw,
  changeFailure,
  currentDetails,
  detailsChangeFields,
  legalNoticeFields,
  pictureBookBand,
  pictureBookRedrawStuck,
  pictureBookTitleFixed,
  redrawConfirmText,
  changeConfirmText,
  redrawsLeft,
  withBookToken,
  type BookDetails,
} from './flow-state';

const POLL_TIMEOUT_MS = 6 * 60 * 1000;

const LOADING_MESSAGES = [
  'Meeting your family...',
  'Dreaming up the story...',
  'Mixing the paints...',
  'Painting the cover...',
  'Lettering the title...',
];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A cover redraw that died (the book stayed DRAFT past the server's stale window): ask the change route once per poll
 * to put the preview back (GET ?recover=1). True when it asked; a failed request is ignored (the poll goes on).
 */
export async function recoverRedrawOnce(
  book: any,
  asked: { current: boolean },
  bookId: string,
  accessToken: string,
  headers: Record<string, string>,
  fetchImpl: typeof fetch = fetch,
  now: number = Date.now(),
): Promise<boolean> {
  if (asked.current || !pictureBookRedrawStuck(book, now)) return false;
  asked.current = true;
  const url = withBookToken(`/api/books/${encodeURIComponent(bookId)}/picture-book-change?recover=1`, accessToken);
  await fetchImpl(url, { headers, cache: 'no-store' }).catch(() => undefined);
  return true;
}

/** FLAG 2 is reported once per book and field, however often the step remounts (Back / Next) in this visit. */
const legalShown = new Set<string>();
/** preview_ready is reported once per book for a re-show (source 'resumed'); a new cover or a redraw always counts. */
const readyReported = new Set<string>();

class PreviewError extends Error {
  reason: FailureReason;
  /** Going back to Your book can fix it (a refused photo or title). */
  goBack: boolean;
  /** The book is unusable (failed or gone): only a new book helps. */
  discard: boolean;
  constructor(message: string, reason: FailureReason, opts: { goBack?: boolean; discard?: boolean } = {}) {
    super(message);
    this.reason = reason;
    this.goBack = !!opts.goBack;
    this.discard = !!opts.discard;
  }
}
/** The page is navigating away (a bought book) or the step unmounted: nothing to show. */
class Stopped extends Error {}

/** User-facing message for a refused POST create-picture-book-preview, and whether Back can fix it. */
export function createErrorMessage(status: number, body: any): { message: string; goBack: boolean } {
  if (status === 429) return { message: "You've made a few books in a short time. Please wait a little while and try again.", goBack: false };
  if (status === 413) return { message: 'That photo is too big. Please go back and choose a smaller one.', goBack: true };
  // 422: the chosen story does not fit the cast (casting): the route's message, and Back to pick another story
  if (status === 400 || status === 422) {
    const text = typeof body?.error === 'string' ? body.error.trim() : '';
    return { message: text || 'Please check your photo and details.', goBack: true };
  }
  if (status === 404) return { message: "Picture books aren't available right now.", goBack: false };
  if (status >= 500) return { message: 'Our studio is busy. Please try again in a minute.', goBack: false };
  return { message: 'Something went wrong while starting your book. Please try again.', goBack: false };
}

type ChangeBody = Partial<BookDetails> & { band?: ReaderBand };
type ChangeResult = { ok: true; status: string; redrawsLeft: number } | { ok: false; status: number; body: any };

export interface PreviewViewProps {
  title: string;
  logline: string;
  coverImageUrl: string | null;
  band: ReaderBand;
  redrawsLeft: number;
  /** The server said 429 limit (or no redraws are left). */
  limitReached: boolean;
  /** FLAG 1: the parent edited the story. */
  locked: boolean;
  /** FLAG 1: a Your book change was not applied because the story is edited. */
  changeLocked?: boolean;
  /** The server refused a Your book change because the book is ordered (not changeable any more). */
  changeNotChangeable?: boolean;
  /** FLAG 2: fields the legal match changed that the parent has not edited since. */
  legalFields: Array<'title' | 'idea'>;
  /** A catalogue master book: the title is the catalogue title, shown read-only (a change would be ignored). */
  titleFixed?: boolean;
  details: BookDetails;
  busy: boolean;
  error: string | null;
  /** A band change that redraws the cover, waiting for the parent's yes. */
  confirmBand: ReaderBand | null;
  onBandPick: (band: ReaderBand) => void;
  onConfirmBand: () => void;
  onCancelBand: () => void;
  onDetailsInput: (field: keyof BookDetails, value: string) => void;
  onApplyChanges: () => void;
  onStartNewBook: () => void;
  onBack?: () => void;
  onNext: () => void;
}

/** FLAG 1 (owner message): a Your book change of an edited book was not applied, and how to make it anyway. */
export function EditedChangeNotice({ onStartNewBook, message = EDITED_MESSAGE }: { onStartNewBook: () => void; message?: string }) {
  return (
    <div role="alert" className="space-y-3 text-center text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-3 max-w-xl mx-auto">
      <p>{message}</p>
      <Button variant="outline" onClick={onStartNewBook}>
        Start a new book
      </Button>
    </div>
  );
}

/** The ready preview: cover, title, logline, reading level and the change controls. Stateless apart from toggles. */
export function PictureBookPreviewView(p: PreviewViewProps) {
  const [bandPickerOpen, setBandPickerOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // Style/title/idea redraw asks first: the old cover is gone for good
  const [changeConfirmOpen, setChangeConfirmOpen] = useState(false);
  const atLimit = p.limitReached || p.redrawsLeft <= 0;
  const legalNotice = (
    <p role="note" className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 max-w-xl mx-auto">
      {LEGAL_MATCH_NOTICE}
    </p>
  );
  const startNewBook = (
    <Button variant="outline" onClick={p.onStartNewBook}>
      Start a new book
    </Button>
  );

  return (
    <div className="space-y-6">
      {p.changeLocked && <EditedChangeNotice onStartNewBook={p.onStartNewBook} />}
      {p.changeNotChangeable && !p.changeLocked && (
        <EditedChangeNotice onStartNewBook={p.onStartNewBook} message={NOT_CHANGEABLE_MESSAGE} />
      )}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">{p.title}</h2>
        {p.logline && <p className="text-gray-600 max-w-xl mx-auto italic">{p.logline}</p>}
        {p.legalFields.length > 0 && legalNotice}
        <p className="text-sm text-gray-500">
          Written for ages {bandAgeLabel(p.band)}
          {!p.locked && !p.changeNotChangeable && (
            <>
              {' '}&middot;{' '}
              {bandPickerOpen ? (
                <select
                  aria-label="Reading level"
                  className="border border-gray-300 rounded px-1 py-0.5 text-sm text-gray-700 bg-white"
                  value={p.band}
                  disabled={p.busy}
                  onChange={(e) => p.onBandPick(e.target.value as ReaderBand)}
                >
                  {READER_BAND_ORDER.map((b) => (
                    <option key={b} value={b}>
                      Ages {bandAgeLabel(b)}
                    </option>
                  ))}
                </select>
              ) : (
                <button type="button" className="underline text-purple-700 hover:text-purple-900" onClick={() => setBandPickerOpen(true)}>
                  change
                </button>
              )}
            </>
          )}
        </p>
        {p.confirmBand && !p.locked && !p.changeNotChangeable && (
          <div className="max-w-md mx-auto border border-purple-200 bg-purple-50 rounded-md p-3 space-y-2 text-sm text-purple-900">
            <p>{redrawConfirmText(p.confirmBand, p.redrawsLeft)}</p>
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="outline" onClick={p.onCancelBand} disabled={p.busy}>
                Keep it as it is
              </Button>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={p.onConfirmBand} disabled={p.busy}>
                Redraw the cover
              </Button>
            </div>
          </div>
        )}
      </div>

      <Card className="overflow-hidden max-w-md mx-auto">
        <CardContent className="p-0">
          {p.coverImageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={p.coverImageUrl} alt={`${p.title} cover`} className="w-full h-auto object-contain bg-gray-50" />
          ) : (
            <div className="aspect-[3/4] flex items-center justify-center bg-gray-100 text-gray-500 text-sm">Cover is on its way...</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          {p.changeNotChangeable ? (
            <div className="text-center">{startNewBook}</div>
          ) : p.locked ? (
            <div className="space-y-3 text-center">
              <p className="text-sm text-gray-700">{EDITED_MESSAGE}</p>
              {startNewBook}
            </div>
          ) : atLimit ? (
            <div className="space-y-3 text-center">
              <p className="text-sm text-gray-700">{REDRAW_LIMIT_MESSAGE}</p>
              {startNewBook}
            </div>
          ) : (
            <>
              <button
                type="button"
                className="w-full flex items-center justify-between text-left font-semibold text-gray-800"
                onClick={() => setPanelOpen((o) => !o)}
                aria-expanded={panelOpen}
              >
                <span className="flex items-center">
                  <Paintbrush className="w-4 h-4 mr-2 text-purple-600" aria-hidden />
                  {p.titleFixed ? 'Change style or idea' : 'Change style, title or idea'}
                </span>
              </button>
              <p className="text-sm text-gray-500">
                You can redraw the cover {p.redrawsLeft} more time{p.redrawsLeft === 1 ? '' : 's'}
              </p>
              {panelOpen && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Style
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-2 bg-white"
                      value={p.details.style}
                      disabled={p.busy}
                      onChange={(e) => p.onDetailsInput('style', e.target.value)}
                    >
                      {/* A book drawn in a held-out style (e.g. European) still shows its own style */}
                      {(PICTURE_BOOK_FLOW_STYLES.includes(p.details.style)
                        ? PICTURE_BOOK_FLOW_STYLES
                        : [p.details.style, ...PICTURE_BOOK_FLOW_STYLES]
                      ).map((s) => (
                        <option key={s} value={s}>
                          {pictureBookStyleName(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                    <input
                      className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-2"
                      value={p.details.title}
                      maxLength={80}
                      disabled={p.busy}
                      readOnly={p.titleFixed}
                      aria-describedby={p.titleFixed ? 'pb-change-title-fixed' : undefined}
                      onChange={(e) => { if (!p.titleFixed) p.onDetailsInput('title', e.target.value); }}
                    />
                  </label>
                  {p.titleFixed && (
                    <p id="pb-change-title-fixed" className="text-sm text-gray-500">
                      {FIXED_TITLE_NOTE}
                    </p>
                  )}
                  {p.legalFields.includes('title') && legalNotice}
                  <label className="block text-sm font-medium text-gray-700">
                    Story idea
                    <AutoGrowTextarea
                      className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-2"
                      rows={3}
                      value={p.details.idea}
                      maxLength={500}
                      disabled={p.busy}
                      onChange={(e) => p.onDetailsInput('idea', e.target.value)}
                    />
                  </label>
                  {p.legalFields.includes('idea') && legalNotice}
                  {changeConfirmOpen && !p.busy ? (
                    <div className="border border-purple-200 bg-purple-50 rounded-md p-3 space-y-2 text-sm text-purple-900" role="alertdialog" aria-label="Redraw the cover?">
                      <p>{changeConfirmText(p.redrawsLeft)}</p>
                      <div className="flex justify-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setChangeConfirmOpen(false)}>
                          Keep this cover
                        </Button>
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => {
                            setChangeConfirmOpen(false);
                            p.onApplyChanges();
                          }}
                        >
                          Yes, redraw it
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white" disabled={p.busy} onClick={() => setChangeConfirmOpen(true)}>
                      <RefreshCw className="w-4 h-4 mr-2" aria-hidden />
                      Redraw the cover
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
          {p.error && <p className="text-sm text-red-600">{p.error}</p>}
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        {p.onBack ? (
          <Button variant="outline" size="lg" onClick={p.onBack} className="w-full sm:w-auto" disabled={p.busy}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          onClick={p.onNext}
          disabled={p.busy}
        >
          <BookOpen className="w-4 h-4 mr-2" aria-hidden />
          Read your story
        </Button>
      </div>
    </div>
  );
}

interface PictureBookPreviewStepProps {
  /** The preview book to show (resume, or Back then Next); null creates one from createPayload. */
  bookId: string | null;
  /** The create route body (people, details, photo); used only when bookId is null. */
  createPayload?: () => object | null;
  /** Your book changed style/title/idea on the existing book: sent to the change route once, before polling. */
  pendingChange?: Partial<BookDetails> | null;
  /** An emailed link's ?bt= token, carried onto every book request. */
  accessToken: string;
  /** FLAG 1: the parent has edited the story. */
  locked: boolean;
  /** The Your book values, used when the book has no stored text yet. */
  fallbackDetails?: BookDetails;
  /** `idea-card:<id>` when Your book's text came from an idea card: sent with preview_requested. */
  ideaSource?: string;
  onBookCreated: (bookId: string) => void;
  /** A change went through: the fields sent, so Your book and the flow's baseline match the book. */
  onDetailsChanged: (change: Partial<BookDetails>) => void;
  /** The server answered 409 edited. */
  onLocked: () => void;
  onStartNewBook: () => void;
  onBack?: () => void;
  onNext: () => void;
}

/**
 * Preview: creates the book (or re-polls an existing one), shows the cover in about a minute, and lets the parent
 * change the reading level, style, title or idea (up to 3 cover redraws) until the story is edited (FLAG 1).
 */
export function PictureBookPreviewStep({
  bookId,
  createPayload,
  pendingChange,
  accessToken,
  locked,
  fallbackDetails,
  ideaSource,
  onBookCreated,
  onDetailsChanged,
  onLocked,
  onStartNewBook,
  onBack,
  onNext,
}: PictureBookPreviewStepProps) {
  const router = useRouter();
  const [phase, setPhase] = useState<'creating' | 'preview' | 'error'>('creating');
  const [book, setBook] = useState<any>(null);
  const [band, setBand] = useState<ReaderBand>('M');
  const [left, setLeft] = useState(3);
  const [limitReached, setLimitReached] = useState(false);
  const [notChangeable, setNotChangeable] = useState(false);
  const [details, setDetails] = useState<BookDetails>(() => fallbackDetails ?? { style: 'watercolor', title: '', idea: '' });
  const [baseline, setBaseline] = useState<BookDetails | null>(null);
  const [typed, setTyped] = useState<Array<'title' | 'idea'>>([]);
  const [busy, setBusy] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [confirmBand, setConfirmBand] = useState<ReaderBand | null>(null);
  const [failure, setFailure] = useState<PreviewError | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  // Past the ~60 s preview rule (the hard timeout is 6 min): reassure the parent
  const [slow, setSlow] = useState(false);

  // FLAG 1: Your book changed the style/title/idea of a book whose story the parent has edited: never dropped silently
  const changeLocked = !!pendingChange && Object.keys(pendingChange).length > 0 && locked;

  const idRef = useRef<string | null>(bookId);
  const startedRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'creating') return;
    setSlow(false);
    const t = setInterval(() => setMessageIndex((i) => (i + 1) % LOADING_MESSAGES.length), 4000);
    const late = setTimeout(() => setSlow(true), PREVIEW_SLOW_MS);
    return () => {
      clearInterval(t);
      clearTimeout(late);
    };
  }, [phase]);

  /** Show a ready book: everything the controls read comes from it. */
  const showBook = (b: any) => {
    setBook(b);
    setBand(pictureBookBand(b));
    setLeft(redrawsLeft(b));
    const d = currentDetails(b, fallbackDetails);
    setDetails(d);
    setBaseline(d);
    setTyped([]);
    setPhase('preview');
  };

  /** Poll the book until its preview is ready to buy; a bought book goes to its own page. */
  const pollUntilReady = async (id: string, headers: Record<string, string>): Promise<any> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let failures = 0;
    let first = true;
    const recoveryAsked = { current: false };
    while (Date.now() < deadline) {
      if (!first) await sleep(comicPreviewPollMs(true));
      first = false;
      if (cancelledRef.current) throw new Stopped();
      let b: any = null;
      let failed: number | 'network' | null = null;
      try {
        const res = await fetch(withBookToken(`/api/books/${encodeURIComponent(id)}`, accessToken), { headers, cache: 'no-store' });
        if (res.ok) b = (await res.json())?.book ?? null;
        else failed = res.status;
      } catch {
        failed = 'network';
      }
      if (failed !== null) {
        failures += 1;
        const action = pollFailureAction(failed, failures);
        if (action === 'stop-invalid') {
          throw new PreviewError('This book link is no longer valid in this browser. Please start a new book.', 'http_4xx', { discard: true });
        }
        if (action === 'stop-lost') throw new PreviewError('We lost touch with your book. Please try again.', 'network');
        continue;
      }
      failures = 0;
      // A cover redraw that died: the server puts the old cover back (not counted); the next poll sees it ready
      if (await recoverRedrawOnce(b, recoveryAsked, id, accessToken, headers)) continue;
      const state = comicReaderState(b);
      if (state === 'failed') {
        throw new PreviewError("We couldn't finish this book's cover. Please start a new book.", 'error', { discard: true });
      }
      const target = pictureBookResumeTarget(b);
      if (target === 'post-purchase' || target === 'old-preview') {
        router.replace(withBookToken(`/book/${encodeURIComponent(id)}/preview`, accessToken));
        throw new Stopped();
      }
      if (state === 'awaiting-purchase') return b;
    }
    throw new PreviewError('This is taking longer than expected. Please try again in a moment.', 'timeout');
  };

  /**
   * POST the change route. A change never waits behind the page script (the generation fence): a 409 busy means
   * another change holds the preview lock, which takes seconds, so it is retried quietly with bandChangeRetry.
   */
  const postChange = async (id: string, change: ChangeBody, headers: Record<string, string>): Promise<ChangeResult> => {
    const started = Date.now();
    for (;;) {
      let res: Response;
      let data: any;
      try {
        res = await fetch(withBookToken(`/api/books/${encodeURIComponent(id)}/picture-book-change`, accessToken), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify(change),
        });
        data = await res.json().catch(() => ({}));
      } catch {
        return { ok: false, status: 0, body: {} };
      }
      if (res.ok) return { ok: true, status: String(data?.status ?? ''), redrawsLeft: Number(data?.redrawsLeft) };
      if (bandChangeRetry(res.status, data, Date.now() - started) === 'stop') return { ok: false, status: res.status, body: data };
      await sleep(Math.max(1000, Number(data?.retryAfterMs) || 2000));
      if (cancelledRef.current) throw new Stopped();
    }
  };

  /** A refused change: FLAG 1 lock, the redraw limit, or a message. */
  const refused = (r: { status: number; body: any }) => {
    const f = changeFailure(r.status, r.body);
    if (f.kind === 'edited') onLocked();
    else if (f.kind === 'limit') setLimitReached(true);
    else if (f.kind === 'not-changeable') setNotChangeable(true);
    else setChangeError(f.message);
  };

  const fail = (e: unknown) => {
    if (cancelledRef.current || e instanceof Stopped) return;
    const err = e instanceof PreviewError ? e : new PreviewError('Something went wrong while making your book. Please try again.', 'network');
    trackCreate('picture-book', 'preview_failed', { reason: err.reason });
    setFailure(err);
    setPhase('error');
  };

  /** Create (or re-poll) the preview, applying a pending Your book change first. */
  const run = async () => {
    const startedAt = Date.now();
    setPhase('creating');
    setFailure(null);
    try {
      const headers = await bookRequestHeaders();
      let id = idRef.current;
      let source: 'new' | 'resumed' | 'redraw' = 'resumed';
      if (!id) {
        const payload = createPayload?.();
        if (!payload) throw new PreviewError('Please go back and check your book details.', 'error', { goBack: true });
        trackCreate('picture-book', 'preview_requested', ideaSource ? { source: ideaSource } : undefined);
        const res = await fetch('/api/books/create-picture-book-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status !== 202 || !data?.bookId) {
          const { message, goBack } = createErrorMessage(res.status, data);
          throw new PreviewError(message, httpFailureReason(res.status), { goBack });
        }
        id = String(data.bookId);
        idRef.current = id;
        source = 'new';
        onBookCreated(id);
      } else if (pendingChange && Object.keys(pendingChange).length > 0 && !locked) {
        // A style/title/idea change always redraws the cover: with none left it is refused here, as a band change is
        const current = await fetch(withBookToken(`/api/books/${encodeURIComponent(id)}`, accessToken), { headers, cache: 'no-store' })
          .then(async (res) => (res.ok ? ((await res.json())?.book ?? null) : null))
          .catch(() => null);
        if (cancelledRef.current) throw new Stopped();
        const r: ChangeResult =
          current && redrawsLeft(current) <= 0 ? { ok: false, status: 429, body: { error: 'limit' } } : await postChange(id, pendingChange, headers);
        // `in` narrows (the repo has strictNullChecks off, so `ok` alone does not)
        if ('body' in r) {
          refused(r);
        } else {
          setLeft(r.redrawsLeft);
          onDetailsChanged(pendingChange);
          if (r.status === 'redrawing') source = 'redraw';
        }
      }
      const ready = await pollUntilReady(id, headers);
      if (cancelledRef.current) return;
      showBook(ready);
      if (source !== 'resumed' || !readyReported.has(id)) {
        readyReported.add(id);
        trackCreate('picture-book', 'preview_ready', { ms: Date.now() - startedAt, source });
      }
    } catch (e) {
      fail(e);
    }
  };

  useEffect(() => {
    // React 18 dev double-effect: creating a book is expensive
    if (startedRef.current) return;
    startedRef.current = true;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Send a change; a redraw goes back to the making state and polls the new cover. */
  const applyChange = async (change: ChangeBody) => {
    const id = idRef.current;
    if (!id || busy || locked) return;
    setBusy(true);
    setChangeError(null);
    try {
      const headers = await bookRequestHeaders();
      const r = await postChange(id, change, headers);
      if (cancelledRef.current) return;
      if ('body' in r) {
        refused(r);
        return;
      }
      setLeft(r.redrawsLeft);
      const { band: newBand, ...fields } = change;
      if (Object.keys(fields).length > 0) onDetailsChanged(fields);
      if (r.status === 'redrawing') {
        const startedAt = Date.now();
        setPhase('creating');
        const ready = await pollUntilReady(id, headers);
        if (cancelledRef.current) return;
        showBook(ready);
        trackCreate('picture-book', 'preview_ready', { ms: Date.now() - startedAt, source: 'redraw' });
      } else if (newBand) {
        // The cover is kept; the story is rewritten for the new level in the background
        setBand(newBand);
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const onBandPick = (next: ReaderBand) => {
    if (next === band || busy || locked) return;
    setChangeError(null);
    if (bandChangeIsRedraw(band, next)) {
      if (limitReached || left <= 0) setLimitReached(true);
      else setConfirmBand(next);
      return;
    }
    void applyChange({ band: next });
  };

  // FLAG 2: the notice shows while a field the legal match changed is unedited; reported once per book and field
  const legalFields = legalNoticeFields(book).filter((f) => !typed.includes(f));
  const legalKey = legalFields.join(',');
  useEffect(() => {
    const id = idRef.current;
    if (phase !== 'preview' || !id) return;
    for (const field of legalFields) {
      const key = `${id}:${field}`;
      if (legalShown.has(key)) continue;
      legalShown.add(key);
      trackCreate('picture-book', 'legal_match_shown', { field });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, legalKey]);

  if (phase === 'creating') {
    return (
      <Card>
        <CardContent className="py-14 px-6 text-center space-y-4">
          <div className="relative w-16 h-16 mx-auto">
            <div className="absolute inset-0 rounded-full border-4 border-purple-100" />
            <div className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
            <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-purple-600" aria-hidden />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Making your picture book</h2>
          <p className="text-purple-700 font-medium" aria-live="polite">
            {slow ? PREVIEW_SLOW_MESSAGE : LOADING_MESSAGES[messageIndex]}
          </p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">We&apos;re writing the story and painting the cover. This usually takes about a minute.</p>
          {changeLocked && <EditedChangeNotice onStartNewBook={onStartNewBook} />}
        </CardContent>
      </Card>
    );
  }

  if (phase === 'error') {
    const discard = !!failure?.discard;
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-10 px-6 text-center space-y-4">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" aria-hidden />
          <h2 className="text-xl font-bold text-gray-800">We couldn&apos;t make your preview</h2>
          <p className="text-sm text-red-700 max-w-md mx-auto">{failure?.message}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
            {discard ? (
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={onStartNewBook}>
                Start a new book
              </Button>
            ) : (
              !failure?.goBack && (
                <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => void run()}>
                  <RefreshCw className="w-4 h-4 mr-2" aria-hidden />
                  Try again
                </Button>
              )
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!book) return null;
  const view = comicPreviewFromBook(book, { title: details.title, pageCount: 16 });
  const titleFixed = pictureBookTitleFixed(book);

  return (
    <PictureBookPreviewView
      title={view.title}
      logline={view.logline}
      coverImageUrl={view.coverImageUrl}
      band={band}
      redrawsLeft={left}
      limitReached={limitReached}
      locked={locked}
      changeLocked={changeLocked}
      changeNotChangeable={notChangeable}
      legalFields={legalFields}
      titleFixed={titleFixed}
      details={details}
      busy={busy}
      error={changeError}
      confirmBand={confirmBand}
      onBandPick={onBandPick}
      onConfirmBand={() => {
        const next = confirmBand;
        setConfirmBand(null);
        if (next) void applyChange({ band: next });
      }}
      onCancelBand={() => setConfirmBand(null)}
      onDetailsInput={(field, value) => {
        setDetails((d) => ({ ...d, [field]: field === 'style' ? (value as PictureBookStyle) : value }));
        if (field !== 'style' && !typed.includes(field)) setTyped((t) => [...t, field]);
      }}
      onApplyChanges={() => {
        const change = detailsChangeFields(baseline ?? details, details);
        if (Object.keys(change).length === 0) {
          setChangeError('Change the style, title or idea first.');
          return;
        }
        if (change.title !== undefined && !change.title) {
          setChangeError('Please give your book a title.');
          return;
        }
        // A style/title/idea change always redraws the cover: refused here when none are left (as a band change is)
        if (limitReached || left <= 0) {
          setLimitReached(true);
          return;
        }
        void applyChange(change);
      }}
      onStartNewBook={onStartNewBook}
      onBack={onBack}
      onNext={onNext}
    />
  );
}
